import React, {useCallback, useEffect} from 'react';

import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {SidepanelInternalState} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';
import * as keyboard from 'util/Keyboard';

import {FlowchartEdge} from '../processFlowchartConfigEditorConstants';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChangeType} from '../processFlowchartConfigEditorUtils';
import {shouldBlockDeleteKeyPress} from './shouldBlockDeleteKeyPress';
import {trackEdgeRemovalAnalytics} from './useFlowchartStateAnalytics';

interface UseDeleteKeyHandlerParams {
  locked: boolean;
  allowNodeDeletion?: boolean;
  reactFlowWrapper?: React.RefObject<HTMLDivElement>;
  leftSidepanelRef?: React.RefObject<HTMLDivElement>;
  rightSidepanelRef?: React.RefObject<HTMLDivElement>;
  sidepanelStateRef: React.MutableRefObject<SidepanelInternalState>;
  rightPanelSize?: SidepanelSize | null;
  interactiveNodes: Array<FlowchartNode>;
  interactiveEdges: Array<FlowchartEdge>;
  flowchartTypeName: string;
  onReactFlowEdgeChangesOnly: (
    edgeChanges: Array<{type: FlowchartEdgeChangeType.REMOVE; id: string}>
  ) => void;
  deleteNodesWithConfirmation: (nodes: Array<FlowchartNode>, edges: Array<FlowchartEdge>) => Promise<void>;
}

/**
 * Listens for Delete/Backspace keypresses on the flowchart canvas. Edge-only
 * deletions happen immediately; deletions that include nodes are routed
 * through `deleteNodesWithConfirmation` for a confirmation modal.
 */
export function useDeleteKeyHandler({
  locked,
  allowNodeDeletion = true,
  reactFlowWrapper,
  leftSidepanelRef,
  rightSidepanelRef,
  sidepanelStateRef,
  rightPanelSize,
  interactiveNodes,
  interactiveEdges,
  flowchartTypeName,
  onReactFlowEdgeChangesOnly,
  deleteNodesWithConfirmation,
}: UseDeleteKeyHandlerParams): void {
  const handleDeleteKeyPress = useCallback(
    (event: KeyboardEvent): void => {
      if (
        locked ||
        !keyboard.isADeleteKey(event) ||
        isFocusSupercededByModal(reactFlowWrapper?.current ?? undefined)
      ) {
        return;
      }

      if (
        shouldBlockDeleteKeyPress({
          target: event.target as HTMLElement,
          reactFlowWrapper: reactFlowWrapper?.current,
          leftSidepanel: leftSidepanelRef?.current,
          rightSidepanel: rightSidepanelRef?.current,
          openSidepanelType: sidepanelStateRef.current.openSidepanelType,
          rightPanelSize,
        })
      ) {
        return;
      }

      const selectedEdges = interactiveEdges.filter((edge) => edge.selected);
      const selectedNodes = interactiveNodes.filter((node) => node.selected);

      if (selectedEdges.length === 0 && selectedNodes.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (selectedNodes.length === 0 || !allowNodeDeletion) {
        if (selectedEdges.length > 0) {
          trackEdgeRemovalAnalytics(
            selectedEdges.map((edge) => edge.type),
            flowchartTypeName
          );

          const edgeChanges = selectedEdges.map((edge) => ({
            type: FlowchartEdgeChangeType.REMOVE as const,
            id: edge.id,
          }));
          onReactFlowEdgeChangesOnly(edgeChanges);
        }
        return;
      }

      if (selectedNodes.length > 0) {
        deleteNodesWithConfirmation(selectedNodes, selectedEdges);
      }
    },
    [
      locked,
      allowNodeDeletion,
      reactFlowWrapper,
      leftSidepanelRef,
      rightSidepanelRef,
      sidepanelStateRef,
      rightPanelSize,
      interactiveNodes,
      interactiveEdges,
      onReactFlowEdgeChangesOnly,
      deleteNodesWithConfirmation,
      flowchartTypeName,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleDeleteKeyPress);
    return () => {
      document.removeEventListener('keydown', handleDeleteKeyPress);
    };
  }, [handleDeleteKeyPress]);
}
